export type WebhookEventName =
  | 'ORDER_PAYMENT_SUCCEEDED'
  | 'ORDER_PAYMENT_FAILED'
  | 'PAYMENT_REQUEST_EXPIRED'
  | 'PAYMENT_SUCCEEDED'
  | 'PAYMENT_FAILED';

export type WebhookEventType = {
  eventName: WebhookEventName;
  eventDescription: string;
};

export type WebhookEndpoint = {
  subscriptionId: string;
  merchantId: string;
  endpoint: string;
  description?: string;
  failureNotificationEmails: string[];
  created: string;
  modified: string;
  eventSubscriptions: WebhookEventType[];
  includeChildMerchantEvents: boolean;
  customRequestHeaders?: { [key: string]: string };
  maskedSecret?: string;
};

export type WebhookEventPageDataType = {
  pageNumber: number;
  pageSize: number;
  totalPages: number | null;
  totalRecords: number | null;
  unseenRecords: number | null;
};

export type UpdateWebhookObject = {
  endpoint: string;
  failureNotificationEmails: string[];
  eventSubscriptions: WebhookEventName[];
  description?: string;
  includeChildMerchantEvents: boolean;
  customRequestHeaders: { [key: string]: string } | null;
  secret?: string;
};

export enum EventStatusValue {
  TODO = 'TODO',
  IN_PROCESS = 'IN_PROCESS',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  AUTO_RETRYING = 'AUTO_RETRYING',
}

export enum EventStatusDisplayValue {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export enum SortByValue {
  ASC = 'ASCENDING',
  DSC = 'DESCENDING',
}

export type WebhookEventsSortField = 'eventStatus' | 'lastAttempt' | 'initialSend';

export type WebhookEventsSortQuery = {
  sortField?: WebhookEventsSortField;
  sortBy?: SortByValue;
};

export type EventNotificationAttemptType = {
  notificationAttemptStatus: string;
  responseCode: number;
  failureMessage: string;
  created: string;
};

export type SelectedFiltersType = {
  selectedStatusFilters: Set<string>;
  selectedEventFilters: Set<string>;
  selectedInitialSendDateTo: Date | null;
  selectedInitialSendDateFrom: Date | null;
  selectedLastAttemptDateTo: Date | null;
  selectedLastAttemptDateFrom: Date | null;
};

export type WebhookEventDataType = {
  eventId: string;
  eventStatus: EventStatusValue;
  displayStatus: EventStatusDisplayValue;
  endpointUrl: string;
  customRequestHeaders?: Record<string, string>;
  maskedSecret?: string;
  failureNotificationEmails?: string[];
  eventType: WebhookEventType;
  merchantId: string;
  notificationAttemptCount: number;
  notificationAttempts: EventNotificationAttemptType[];
  subscriptionId: string;
  created: string;
  lastNotificationAttemptAt?: string | null;
};

export type RetryWebhookEventOptions = {
  useCurrentSubscription?: boolean;
};

/** Thrown when the API returns 409 because a delivery is already in progress */
export class WebhookRetryConflictError extends Error {
  constructor() {
    super('Webhook retry conflict');
    this.name = 'WebhookRetryConflictError';
  }
}

export type WebhookNotificationAttemptResponse = {
  notificationAttemptStatus: 'SUCCESS' | 'FAILURE';
  responseCode: number;
  failureMessage?: string;
  created: string;
};

export type WebhookEventResponse = {
  eventId: string;
  merchantId: string;
  subscriptionId: string;
  eventType: WebhookEventType;
  eventStatus: 'TODO' | 'IN_PROCESS' | 'SUCCESS' | 'FAILURE';
  notificationAttemptCount: number;
  notificationAttempts?: WebhookNotificationAttemptResponse[];
  endpointUrl: string;
  customRequestHeaders?: Record<string, string>;
  maskedSecret?: string;
  failureNotificationEmails?: string[];
  created: string;
  displayStatus: 'PENDING' | 'SUCCESS' | 'FAILURE';
  lastNotificationAttemptAt?: string | null;
};

export type WebhookEventsPageDescriptionResponse = {
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  unseenRecords?: number;
};

export type WebhookEventsPageLinksResponse = {
  previousPage?: string;
  nextPage?: string;
  firstPage?: string;
  lastPage?: string;
};

export type GetWebhookEventsApiResponse = {
  description: WebhookEventsPageDescriptionResponse;
  links?: WebhookEventsPageLinksResponse;
  data: WebhookEventResponse[];
};

export type GetWebhookEventsQuery = {
  merchantId: string;
  pageSize?: number;
  pageNumber?: number;
  sortBy?: SortByValue;
  sortField?: WebhookEventsSortField;
  initialSendTo?: string;
  initialSendFrom?: string;
  lastAttemptTo?: string;
  lastAttemptFrom?: string;
  statuses?: string[];
  eventTypes?: string[];
};

export type CreateWebhookBody = {
  merchantId: string;
  endpoint: string;
  failureNotificationEmails: string[];
  eventSubscriptions: WebhookEventName[];
  secret: string;
  description?: string;
  includeChildMerchantEvents: boolean;
  customRequestHeaders?: { [key: string]: string };
};

export type UpdateWebhookBody = {
  subscriptionId: string;
  merchantId: string;
  endpoint: string;
  failureNotificationEmails: string[];
  eventSubscriptions: WebhookEventName[];
  description?: string;
  includeChildMerchantEvents: boolean;
  customRequestHeaders?: { [key: string]: string } | null;
  secret?: string;
};

export type GetWebhookEventsResponse = {
  data: WebhookEventDataType[];
  pageData: WebhookEventPageDataType;
};

export const WEBHOOK_EVENT_TYPES: WebhookEventType[] = [
  {
    eventName: 'ORDER_PAYMENT_SUCCEEDED',
    eventDescription: 'Order Payment Succeeded',
  },
  {
    eventName: 'ORDER_PAYMENT_FAILED',
    eventDescription: 'Order Payment Failed',
  },
  {
    eventName: 'PAYMENT_REQUEST_EXPIRED',
    eventDescription: 'Payment Request Expired',
  },
  {
    eventName: 'PAYMENT_SUCCEEDED',
    eventDescription: 'Payment Succeeded',
  },
  {
    eventName: 'PAYMENT_FAILED',
    eventDescription: 'Payment Failed',
  },
];
